import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import WorkIcon from '@mui/icons-material/Work';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../utils/localization/localization';
import LanguageSlideSwitch from './LanguageSlideSwitch';
import { logger } from '../utils/logger';
import { useLanguageAwareRender } from '../hooks/useLanguageAwareRender';

function Header() {
  const { currentUser, userRole, oidcLogoutRedirect, ROLES } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  // Use standardized language-aware rendering hook
  const languageRenderKey = useLanguageAwareRender('Header');

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await oidcLogoutRedirect();
    } catch (error) {
      logger.error('Failed to log out', error);
    } finally {
      handleClose();
    }
  };

  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <AppBar position="static" key={languageRenderKey}>
      <Toolbar>
        <WorkIcon sx={{ mr: 2 }} />
        <LanguageSlideSwitch />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {t('header.title')}
        </Typography>

        {currentUser ? (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button color="inherit" component={RouterLink} to="/">
                {t('dashboard.cvAnalyzer')}
              </Button>

              {/* Show Job Skills to all users */}
              <Button color="inherit" component={RouterLink} to="/job-analyzer">
                {t('dashboard.jobAnalyzer')}
              </Button>

              {/* Only show Company Research to admins */}
              {userRole === ROLES.ADMIN && (
                <Button color="inherit" component={RouterLink} to="/company-research">
                  {t('dashboard.companyResearch')}
                </Button>
              )}

              {/* Show Result page to all users */}
              <Button color="inherit" component={RouterLink} to="/result">
                {t('dashboard.trainingRecommendations')}
              </Button>

              {/* Only show Admin to Admin role */}
              {userRole === ROLES.ADMIN && (
                <Button color="inherit" component={RouterLink} to="/admin">
                  {t('common.dashboard')}
                </Button>
              )}

              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleMenu}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                    {getInitials(currentUser.name)}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>

            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0
                  }
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem component={RouterLink} to="/profile">
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                {t('header.myProfile')}
              </MenuItem>

              {userRole === ROLES.ADMIN && (
                <MenuItem component={RouterLink} to="/admin">
                  <ListItemIcon>
                    <AdminPanelSettingsIcon fontSize="small" />
                  </ListItemIcon>
                  {t('header.adminDashboard')}
                </MenuItem>
              )}

              {(userRole === ROLES.MANAGER || userRole === ROLES.ADMIN) && (
                <MenuItem component={RouterLink} to="/team">
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  {t('header.teamManagement')}
                </MenuItem>
              )}

              <Divider />

              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t('header.signOut')}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button color="inherit" component={RouterLink} to="/login">
              {t('auth.login')}
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Header;
